import { useParams, useNavigate } from "react-router-dom";
import { mockSecurityIssues } from "@/data/mockSecurityIssues";
import { getIssueDetails } from "@/data/mockIssueDetails";
import { AttackGraphVisual } from "@/components/security/AttackGraphVisual";
import { getSeverityColor, formatRiskScore, getRiskScoreColor } from "@/utils/securityHelpers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  CheckCircle2,
  MessageSquare,
  Play,
  Ticket,
  History,
  LifeBuoy,
  Clock,
  Shield,
  Database,
  Users,
  Container,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  ExternalLink,
  DollarSign,
  Scale,
  Download,
  AlertTriangle,
  TrendingUp,
  Activity,
} from "lucide-react";
import { useState } from "react";

export function IssueDetailPage() {
  const { issueId } = useParams<{ issueId: string }>();
  const navigate = useNavigate();
  const [expandedTraces, setExpandedTraces] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>("overview");

  const issue = mockSecurityIssues.find((i) => i.id === issueId);
  const details = issue ? getIssueDetails(issue.id) : undefined;

  if (!issue || !details) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Issue Not Found</h2>
          <p className="text-muted-foreground mb-4">The requested issue could not be found.</p>
          <Button onClick={() => navigate("/issues")}>Back to Issues</Button>
        </div>
      </div>
    );
  }

  const toggleTrace = (traceId: string) => {
    setExpandedTraces((prev) =>
      prev.includes(traceId) ? prev.filter((id) => id !== traceId) : [...prev, traceId]
    );
  };

  const handleComment = () => {
    alert("Comment feature - Coming soon! This will open a comment dialog.");
  };

  const handleRunAction = () => {
    alert("Run Action feature - Coming soon! This will show available automated actions.");
  };

  const handleCreateTicket = () => {
    alert(
      "Create Ticket feature - Coming soon! This will integrate with your ticketing system (Jira, ServiceNow, etc.)."
    );
  };

  const handlePastActivities = () => {
    alert("Past Activities - Currently 0 activities recorded for this issue.");
  };

  const handleSupport = () => {
    alert("Support - Coming soon! This will connect you with security support team.");
  };

  const handleExportReport = () => {
    const report = {
      issue,
      details,
      businessImpact: {
        financialImpact: "$250K-$500K",
        affectedUsers: 5000,
        businessUnits: ["Support", "Sales"],
        slaBreaches: 2,
      },
      compliance: ["GDPR Article 32", "SOC 2 CC6.1", "ISO 27001"],
      metrics: {
        timeToDetect: "2.3 hours",
        timeToRespond: "4.5 hours",
        timeToRemediate: "48 hours",
      },
      exportedAt: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${issue.id}-full-report.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-1 flex-col gap-6 p-6 bg-gray-50">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate("/issues")} className="gap-2 w-fit -ml-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Issues
      </Button>

      {/* Issue Header with Icon */}
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
          <AlertCircle className="h-6 w-6 text-orange-600" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold mb-2">{issue.title}</h1>
          <div className="text-sm text-muted-foreground">Issue</div>
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="gap-2" onClick={handleComment}>
          <MessageSquare className="h-4 w-4" />
          Comment
        </Button>
        <Button variant="outline" size="sm" className="gap-2" onClick={handleRunAction}>
          <Play className="h-4 w-4" />
          Run an Action
        </Button>
        <Button variant="outline" size="sm" className="gap-2" onClick={handleCreateTicket}>
          <Ticket className="h-4 w-4" />
          Create a Ticket
        </Button>
        <Button variant="outline" size="sm" className="gap-2" onClick={handlePastActivities}>
          <History className="h-4 w-4" />0 Past Activities
        </Button>
        <Button variant="outline" size="sm" className="gap-2" onClick={handleSupport}>
          <LifeBuoy className="h-4 w-4" />
          Support
        </Button>
        <Button variant="outline" size="sm" className="gap-2 ml-auto" onClick={handleExportReport}>
          <Download className="h-4 w-4" />
          Export Report
        </Button>
      </div>

      {/* Tabs */}
      <div className="w-full">
        {/* Tab Navigation */}
        <div className="bg-white border-b flex items-center gap-0">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "overview"
                ? "border-primary text-primary"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("investigation")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "investigation"
                ? "border-primary text-primary"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            Investigation
          </button>
          <button
            onClick={() => setActiveTab("remediation")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "remediation"
                ? "border-primary text-primary"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            Remediation
          </button>
          <button
            onClick={() => setActiveTab("comments")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "comments"
                ? "border-primary text-primary"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            Comments
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "history"
                ? "border-primary text-primary"
                : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            History
          </button>
        </div>

        {/* Tab Content */}
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="mt-6 space-y-6">
            {/* Issue Details Card */}
            <Card>
              <CardContent className="pt-6 space-y-6">
                {/* Description */}
                <div>
                  <p className="text-sm leading-relaxed">{issue.description}</p>
                </div>

                {/* Metadata Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Severity</div>
                    <Badge className={getSeverityColor(issue.severity)}>{issue.severity}</Badge>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Status</div>
                    <Badge variant="outline">{issue.status}</Badge>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Risk Score</div>
                    <div className={`font-semibold ${getRiskScoreColor(issue.riskScore)}`}>
                      {formatRiskScore(issue.riskScore)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Scanner</div>
                    <div className="text-sm font-medium">{issue.scanner}</div>
                  </div>
                </div>

                {/* Business Impact Section */}
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Business Impact
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-red-50 rounded border border-red-200">
                      <div className="text-xs text-muted-foreground mb-1">Financial Impact</div>
                      <div className="text-sm font-semibold text-red-600">$250K - $500K</div>
                      <div className="text-xs text-muted-foreground mt-1">Estimated exposure</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded border border-blue-200">
                      <div className="text-xs text-muted-foreground mb-1">Affected Users</div>
                      <div className="text-sm font-semibold text-blue-600">~5,000</div>
                      <div className="text-xs text-muted-foreground mt-1">Active customers</div>
                    </div>
                    <div className="p-3 bg-purple-50 rounded border border-purple-200">
                      <div className="text-xs text-muted-foreground mb-1">Business Units</div>
                      <div className="text-sm font-semibold text-purple-600">Support, Sales</div>
                      <div className="text-xs text-muted-foreground mt-1">2 departments</div>
                    </div>
                    <div className="p-3 bg-orange-50 rounded border border-orange-200">
                      <div className="text-xs text-muted-foreground mb-1">SLA Status</div>
                      <Badge variant="destructive" className="text-xs">
                        2 Breaches
                      </Badge>
                      <div className="text-xs text-muted-foreground mt-1">Last 30 days</div>
                    </div>
                  </div>
                </div>

                {/* Remediation Status */}
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Remediation Status
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium">Security Team</div>
                          <div className="text-xs text-muted-foreground">Auto-escalated</div>
                        </div>
                      </div>
                      <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                        In Progress
                      </Badge>
                    </div>

                    {/* Progress Bar */}
                    <div>
                      <div className="flex justify-between text-xs mb-2">
                        <span className="text-muted-foreground">Remediation Progress</span>
                        <span className="font-medium">Step 2 of 4 (50%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: "50%" }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm p-2 bg-yellow-50 rounded border border-yellow-200">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-yellow-600" />
                        <span className="text-muted-foreground">Due Date:</span>
                      </div>
                      <span className="font-medium text-yellow-700">
                        Oct 20, 2025 (2 days remaining)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Compliance Impact */}
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Scale className="h-4 w-4" />
                    Compliance & Regulatory Impact
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-red-50 rounded border border-red-200">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-sm font-medium">
                          GDPR Article 32 (Security of Processing)
                        </span>
                      </div>
                      <Badge variant="destructive" className="text-xs">
                        HIGH RISK
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-yellow-50 rounded border border-yellow-200">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        <span className="text-sm font-medium">SOC 2 CC6.1 (Logical Access)</span>
                      </div>
                      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300 text-xs">
                        MEDIUM
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                        <span className="text-sm font-medium">
                          ISO 27001 A.12.6.1 (Technical Vulnerabilities)
                        </span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        LOW
                      </Badge>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between text-xs p-2 bg-green-50 rounded border border-green-200">
                    <span className="text-muted-foreground">Audit Trail:</span>
                    <span className="text-green-600 font-medium flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Complete & Ready for Export
                    </span>
                  </div>
                </div>

                {issue.detectedContent && (
                  <div className="pt-4 border-t">
                    <h3 className="text-sm font-semibold mb-2">Detected Content</h3>
                    <div className="bg-red-50 border border-red-200 rounded p-3 text-sm font-mono">
                      {issue.detectedContent}
                    </div>
                  </div>
                )}

                {/* Traces Section - Displayed immediately after description */}
                {details.traces.length > 0 && (
                  <div className="pt-4 border-t">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Execution Traces
                    </h3>
                    <div className="border rounded-lg divide-y bg-white">
                      {details.traces.map((trace) => (
                        <div key={trace.id}>
                          <div
                            className="px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-3"
                            onClick={() => toggleTrace(trace.id)}
                          >
                            {/* Expand/Collapse Icon */}
                            <div className="flex-shrink-0">
                              {expandedTraces.includes(trace.id) ? (
                                <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                              ) : (
                                <ChevronUp
                                  className="h-3.5 w-3.5 text-gray-400"
                                  style={{ transform: "rotate(-90deg)" }}
                                />
                              )}
                            </div>

                            {/* Status Indicator Dot */}
                            <div className="flex-shrink-0">
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  trace.status === "blocked"
                                    ? "bg-red-500"
                                    : trace.status === "warning"
                                      ? "bg-yellow-500"
                                      : "bg-green-500"
                                }`}
                              />
                            </div>

                            {/* Type Badge */}
                            <div className="flex-shrink-0">
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 uppercase tracking-wide">
                                {trace.type}
                              </span>
                            </div>

                            {/* Trace Name */}
                            <span className="text-sm font-normal flex-1">{trace.name}</span>

                            {/* Duration */}
                            <div className="flex-shrink-0">
                              <span className="text-xs text-gray-500">
                                {(trace.duration * 1000).toFixed(0)}ms
                              </span>
                            </div>

                            {/* Status Badge */}
                            <div className="flex-shrink-0">
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
                                  trace.status === "blocked"
                                    ? "bg-red-100 text-red-700"
                                    : trace.status === "warning"
                                      ? "bg-yellow-100 text-yellow-700"
                                      : "bg-green-100 text-green-700"
                                }`}
                              >
                                {trace.status}
                              </span>
                            </div>
                          </div>

                          {/* Expanded Content */}
                          {expandedTraces.includes(trace.id) && (
                            <div className="px-4 py-3 bg-gray-50 border-t">
                              <div className="space-y-3 ml-7">
                                <div>
                                  <div className="text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">
                                    INPUT
                                  </div>
                                  <div className="bg-white p-3 rounded border border-gray-200 text-xs font-mono text-gray-800 leading-relaxed">
                                    {trace.input}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[10px] font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">
                                    OUTPUT
                                  </div>
                                  <div className="bg-white p-3 rounded border border-gray-200 text-xs font-mono text-gray-800 leading-relaxed">
                                    {trace.output}
                                  </div>
                                </div>
                                <div className="text-[11px] text-gray-500">
                                  {new Date(trace.timestamp).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Attack Graph */}
            {details.attackFlow && details.attackFlow.length > 0 && (
              <AttackGraphVisual attackFlow={details.attackFlow} />
            )}

            {/* Risk Sections */}
            <div className="grid gap-4 md:grid-cols-2">
              {/* Vulnerability Risk */}
              {details.risks.vulnerability.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Shield className="h-4 w-4 text-red-600" />
                      Vulnerability Risk
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {details.risks.vulnerability.map((risk) => (
                      <div key={risk.id} className="border-l-4 border-red-500 pl-3 py-2">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-sm">{risk.title}</h4>
                          <Badge className={getSeverityColor(risk.severity)} variant="outline">
                            {risk.severity}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{risk.description}</p>
                        <div className="text-xs">
                          <span className="font-medium">Impact: </span>
                          {risk.impact}
                        </div>
                        <div className="text-xs mt-1">
                          <span className="font-medium text-blue-600">Fix: </span>
                          {risk.recommendation}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* External Exposure Risk */}
              {details.risks.exposed.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <ExternalLink className="h-4 w-4 text-orange-600" />
                      External Exposure Risk
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {details.risks.exposed.map((risk) => (
                      <div key={risk.id} className="border-l-4 border-orange-500 pl-3 py-2">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-sm">{risk.title}</h4>
                          <Badge className={getSeverityColor(risk.severity)} variant="outline">
                            {risk.severity}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{risk.description}</p>
                        <div className="text-xs">
                          <span className="font-medium">Impact: </span>
                          {risk.impact}
                        </div>
                        <div className="text-xs mt-1">
                          <span className="font-medium text-blue-600">Fix: </span>
                          {risk.recommendation}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Unprotected Data Risk */}
              {details.risks.unprotectedData.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Database className="h-4 w-4 text-purple-600" />
                      Unprotected Data Risk
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {details.risks.unprotectedData.map((risk) => (
                      <div key={risk.id} className="border-l-4 border-purple-500 pl-3 py-2">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-sm">{risk.title}</h4>
                          <Badge className={getSeverityColor(risk.severity)} variant="outline">
                            {risk.severity}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{risk.description}</p>
                        <div className="text-xs">
                          <span className="font-medium">Impact: </span>
                          {risk.impact}
                        </div>
                        <div className="text-xs mt-1">
                          <span className="font-medium text-blue-600">Fix: </span>
                          {risk.recommendation}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Unprotected Principal Risk */}
              {details.risks.unprotectedPrincipal.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Users className="h-4 w-4 text-yellow-600" />
                      Unprotected Principal Risk
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {details.risks.unprotectedPrincipal.map((risk) => (
                      <div key={risk.id} className="border-l-4 border-yellow-500 pl-3 py-2">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-sm">{risk.title}</h4>
                          <Badge className={getSeverityColor(risk.severity)} variant="outline">
                            {risk.severity}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{risk.description}</p>
                        <div className="text-xs">
                          <span className="font-medium">Impact: </span>
                          {risk.impact}
                        </div>
                        <div className="text-xs mt-1">
                          <span className="font-medium text-blue-600">Fix: </span>
                          {risk.recommendation}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Container Risk */}
              {details.risks.container.length > 0 && (
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Container className="h-4 w-4 text-blue-600" />
                      Container Risk
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid gap-3 md:grid-cols-2">
                    {details.risks.container.map((risk) => (
                      <div key={risk.id} className="border-l-4 border-blue-500 pl-3 py-2">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-sm">{risk.title}</h4>
                          <Badge className={getSeverityColor(risk.severity)} variant="outline">
                            {risk.severity}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{risk.description}</p>
                        <div className="text-xs">
                          <span className="font-medium">Impact: </span>
                          {risk.impact}
                        </div>
                        <div className="text-xs mt-1">
                          <span className="font-medium text-blue-600">Fix: </span>
                          {risk.recommendation}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Investigation Tab */}
        {activeTab === "investigation" && (
          <div className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Investigation Details</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  This section contains detailed investigation information and forensic data.
                </p>

                {/* Trace ID and Metadata */}
                <div className="space-y-3">
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-1">Trace ID</div>
                    <div className="font-mono text-sm">{issue.traceId}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-1">
                      Timestamp
                    </div>
                    <div className="text-sm">{new Date(issue.timestamp).toLocaleString()}</div>
                  </div>
                  {issue.metadata.endpoint && (
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground mb-1">
                        Endpoint
                      </div>
                      <div className="font-mono text-sm">{issue.metadata.endpoint}</div>
                    </div>
                  )}
                  {issue.metadata.model && (
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground mb-1">Model</div>
                      <div className="text-sm">{issue.metadata.model}</div>
                    </div>
                  )}
                  {issue.metadata.userId && (
                    <div>
                      <div className="text-xs font-semibold text-muted-foreground mb-1">
                        User ID
                      </div>
                      <div className="font-mono text-sm">{issue.metadata.userId}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* AI Security Context */}
            <Card className="border-purple-200 bg-purple-50/30">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="h-4 w-4 text-purple-600" />
                  AI Security Context
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-3 bg-white rounded border">
                    <div className="text-xs font-semibold text-muted-foreground mb-1">Model</div>
                    <div className="text-sm font-mono font-medium">GPT-4</div>
                    <div className="text-xs text-muted-foreground mt-1">Version: 2023-11-06</div>
                  </div>
                  <div className="p-3 bg-white rounded border">
                    <div className="text-xs font-semibold text-muted-foreground mb-1">
                      Last Updated
                    </div>
                    <div className="text-sm font-medium">30 days ago</div>
                    <div className="text-xs text-green-600 mt-1">✓ Within policy</div>
                  </div>
                  <div className="p-3 bg-white rounded border">
                    <div className="text-xs font-semibold text-muted-foreground mb-1">
                      Model Drift
                    </div>
                    <div className="text-sm font-medium">2.3%</div>
                    <div className="text-xs text-green-600 mt-1">Normal range</div>
                  </div>
                  <div className="p-3 bg-white rounded border">
                    <div className="text-xs font-semibold text-muted-foreground mb-1">
                      Guardrails
                    </div>
                    <div className="text-sm font-semibold text-yellow-600">8/10 Active</div>
                    <div className="text-xs text-yellow-600 mt-1">2 disabled</div>
                  </div>
                  <div className="p-3 bg-white rounded border">
                    <div className="text-xs font-semibold text-muted-foreground mb-1">
                      Prompt Template
                    </div>
                    <div className="text-sm font-mono font-medium">v2.4.1</div>
                    <div className="text-xs text-green-600 mt-1">✓ Approved</div>
                  </div>
                  <div className="p-3 bg-white rounded border">
                    <div className="text-xs font-semibold text-muted-foreground mb-1">
                      Training Data
                    </div>
                    <div className="text-sm font-medium">OpenAI + Custom</div>
                    <div className="text-xs text-muted-foreground mt-1">Fine-tuned</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Similar Issues */}
            <Card className="border-orange-200 bg-orange-50/30">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-orange-600" />
                  Similar Issues & Patterns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div
                    className="flex items-center justify-between p-3 bg-white border rounded hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate("/issues/SEC-004")}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Badge variant="destructive" className="text-xs">
                        CRITICAL
                      </Badge>
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          SEC-004: Similar prompt injection pattern
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Same attack vector detected
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">2 days ago</span>
                  </div>
                  <div
                    className="flex items-center justify-between p-3 bg-white border rounded hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate("/issues/SEC-007")}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Badge className="bg-orange-500 text-xs">HIGH</Badge>
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          SEC-007: Same user behavioral pattern
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Potential coordinated attack
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">5 days ago</span>
                  </div>
                  <div
                    className="flex items-center justify-between p-3 bg-white border rounded hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate("/issues/SEC-009")}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Badge className="bg-orange-500 text-xs">HIGH</Badge>
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          SEC-009: Related jailbreak attempt
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Similar evasion technique
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">1 week ago</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-white rounded border border-orange-300">
                  <div className="flex items-center gap-2 text-sm">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <span className="font-medium text-orange-800">
                      This attack pattern has been detected 3 times in the last 30 days
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                    Trend analysis suggests an organized campaign. Consider implementing additional
                    preventive measures.
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Remediation Tab */}
        {activeTab === "remediation" && (
          <div className="mt-6 space-y-6">
            <Card className="border-blue-200 bg-blue-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  AI Suggested Fix
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg mb-2">{details.suggestedFix.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {details.suggestedFix.description}
                  </p>
                </div>
                <div className="space-y-3">
                  {details.suggestedFix.steps.map((step) => (
                    <Card key={step.step} className="bg-white">
                      <CardContent className="pt-4">
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700">
                            {step.step}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium mb-1">{step.title}</h4>
                            <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                            {step.code && (
                              <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-x-auto">
                                <code>{step.code}</code>
                              </pre>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Action Items */}
            <Card className="border-yellow-200 bg-yellow-50/30">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  Required Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-white rounded border border-red-200">
                    <input type="checkbox" className="mt-1.5 h-4 w-4" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">Update prompt template to v2.5.0</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Implement enhanced input validation and sanitization rules
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-muted-foreground">Due: Today</span>
                        <span className="text-xs text-muted-foreground">Owner: Security Team</span>
                      </div>
                    </div>
                    <Badge variant="destructive" className="text-xs">
                      URGENT
                    </Badge>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white rounded border border-orange-200">
                    <input type="checkbox" className="mt-1.5 h-4 w-4" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">Enable additional input validation</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Configure LLM Guard with stricter thresholds for prompt injection detection
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-muted-foreground">Due: Tomorrow</span>
                        <span className="text-xs text-muted-foreground">Owner: DevOps</span>
                      </div>
                    </div>
                    <Badge className="bg-orange-500 text-xs">HIGH</Badge>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white rounded border border-blue-200">
                    <input type="checkbox" className="mt-1.5 h-4 w-4" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        Review and update user access controls
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Audit user permissions and revoke unnecessary access to sensitive endpoints
                      </div>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-muted-foreground">Due: Oct 22, 2025</span>
                        <span className="text-xs text-muted-foreground">Owner: IAM Team</span>
                      </div>
                    </div>
                    <Badge className="bg-blue-500 text-xs">MEDIUM</Badge>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded border border-green-200">
                    <input type="checkbox" checked disabled className="mt-1.5 h-4 w-4" />
                    <div className="flex-1">
                      <div className="text-sm font-medium line-through text-muted-foreground">
                        Document incident for compliance audit
                      </div>
                      <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Completed by John Doe - 2 hours ago
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Full incident report exported and stored in compliance repository
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-700 border-green-300 text-xs">
                      DONE
                    </Badge>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                  <div className="flex items-center gap-2 text-sm font-medium text-blue-800 mb-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Next Steps
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
                    <li>Schedule team review meeting to discuss pattern analysis</li>
                    <li>Update security runbook with new detection signatures</li>
                    <li>Notify affected business units of remediation progress</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Comments Tab */}
        {activeTab === "comments" && (
          <div className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Comments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-20" />
                  <p className="text-sm">No comments yet</p>
                  <p className="text-xs mt-1">Be the first to add a comment on this issue</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* History Tab */}
        {activeTab === "history" && (
          <div className="mt-6 space-y-6">
            {/* Performance Metrics */}
            <Card className="border-purple-200 bg-purple-50/30">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="h-4 w-4 text-purple-600" />
                  Response Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-white rounded border border-red-300">
                    <div className="text-3xl font-bold text-red-600 mb-1">2.3h</div>
                    <div className="text-sm font-medium text-muted-foreground mb-2">
                      Time to Detect (MTTD)
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <div className="text-xs text-red-600 font-medium">Target: &lt;1h</div>
                      <Badge variant="destructive" className="text-xs">
                        131% OVER
                      </Badge>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      Issue detected by automated scanner
                    </div>
                  </div>

                  <div className="text-center p-4 bg-white rounded border border-yellow-300">
                    <div className="text-3xl font-bold text-yellow-600 mb-1">4.5h</div>
                    <div className="text-sm font-medium text-muted-foreground mb-2">
                      Time to Respond (MTTR)
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <div className="text-xs text-yellow-600 font-medium">Target: &lt;2h</div>
                      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300 text-xs">
                        125% OVER
                      </Badge>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      Security team acknowledged
                    </div>
                  </div>

                  <div className="text-center p-4 bg-white rounded border border-orange-300">
                    <div className="text-3xl font-bold text-orange-600 mb-1">48h</div>
                    <div className="text-sm font-medium text-muted-foreground mb-2">
                      Time to Remediate (MTTR)
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <div className="text-xs text-orange-600 font-medium">Target: &lt;24h</div>
                      <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-xs">
                        IN PROGRESS
                      </Badge>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      Estimated completion: 2 days
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-white rounded border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Overall Response Performance</span>
                    <Badge className="bg-red-100 text-red-700 border-red-300">
                      NEEDS IMPROVEMENT
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Response times are above targets. Consider implementing automated remediation
                    workflows and enhancing detection capabilities to improve metrics.
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Activity Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Activity Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3 pb-4 border-b">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <AlertCircle className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">Issue Created</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {new Date(issue.timestamp).toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Scanner: {issue.scanner} • Severity: {issue.severity}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pb-4 border-b">
                    <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                      <Users className="h-4 w-4 text-yellow-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">Assigned to Security Team</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        2 hours after detection
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Auto-escalated based on severity level
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 pb-4 border-b">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <Activity className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">Investigation Started</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        4.5 hours after detection
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Similar patterns identified in 3 related issues
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">Remediation In Progress</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Step 2 of 4 completed (50%)
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Expected resolution: Oct 20, 2025
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
